import React, { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Tooltip } from "@mui/joy";
import {
  ExpandableSidebar,
  Button,
  Input,
  base64ToBlob,
  uniqId,
  SelectOption,
  useCall,
  Typography,
  globalStyles,
  clsx,
  useBoolean,
  imageSourceToCanvas,
  CanvasDrawImageSource,
  loadImage,
  resizeImage,
  Canvas2d,
  Vec2,
  useDidUpdate,
  useDidUnmount,
  SvgIcon,
  Checkbox,
  useFetch,
} from "@gemlightbox/core-kit";

import {
  getProduct,
  postClassifyImage,
  postProduct,
  PostRemoveBackgroundResponseData,
  postRetouchAISingle,
  putAssignMediaToProduct,
} from "src/api";
import { ProductTypeSelect } from "./product-type-select";
import { useStores } from "src/hooks";
import { DeviceMetaDataModel, MediaDownloadType, MediaType } from "src/models";
import {
  getAttributesForRequest,
  pushDataLayerEvent,
  pushMediaLocalSaveLayerEvent,
  removeImageBackground,
} from "src/utils";
import { ModalExtendedType } from "src/store";
import { MediaPreview } from "./media-preview";
import { getCategory, prepareBlink } from "./camera-preview-sidebar.utils";
import { DropdownOptionType, SKUSelect } from "./sku-select";
import { CameraMediaType } from "../camera.types";
import { cameraStore } from "../camera.store";
import { changeDpiDataUrl } from "changedpi";
import { ReactComponent as InvalidDirectorySVG } from "src/external-ts/assets/images/camera/invalid-directory-icon.svg";
import { ReactComponent as AiDescriptionSVG } from "src/external-ts/assets/images/camera/ai-description-icon.svg";
import { ReactComponent as ProSVG } from "src/external-ts/assets/images/camera/pro.svg";

import styles from "./camera-preview-sidebar.module.css";
import { ProUserUpgradeModal } from "src/external-ts/components";
import { defaultMiniStorageSpace } from "../camera.constants";
import { useMutation } from "urql";
import { AI_RECOGNITION_TYPE, PRODUCT_GEN_SKU } from "src/api/graphql-api/product/product.gql";
import { useLocation } from "react-router-dom";
import { productTitleMaxLength, productDescriptionMaxLength } from "src/containers/products";
import { ProductSubTypeSelect } from "./product-sub-type-select";

export type CameraPreviewSidebarProps = ModalExtendedType<{
  mediaList: CameraMediaType[];
  onClose: VoidFunction;
  loading: boolean;
  captureMode: string;
}>;

export const CameraPreviewSidebar: React.FC<CameraPreviewSidebarProps> = observer(
  ({ isOpen, setClose, onFinalClosed, options }) => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const queryProductId = queryParams.get("productId");
    const {
      localeStore,
      mediaStore,
      modalsStore,
      notificationStore,
      attributesStore,
      categoriesStore,
      userStore,
      subscriptionsStore,
      productsStore,
    } = useStores();
    const { mediaList, onClose, loading = true, captureMode } = options;
    const {
      isBurstMode: isVideoBurstMode,
      generateAIDescription,
      presetMode,
      videoAngle,
      bluetoothDeviceSelected,
      gemAIActive,
      dpi,
      openEclipse,
      isAutoSkuConfirmation,
    } = cameraStore;
    const isBurstMode = captureMode === "video" ? isVideoBurstMode : true;

    const useInSaveBoolean = useBoolean();
    const useInBlinkBoolean = useBoolean();
    const useInRmBGBoolean = useBoolean();
    const useTooltipBoolean = useBoolean(false);
    const useAIHandleLoadingBoolean = useBoolean(true);
    const useDescAIHandleLoadingBoolean = useBoolean(true);
    const useNoSaveProductInfo = useBoolean(cameraStore.isNoSaveProductInfo);

    const [finalMedias, setFinalMedias] = useState(mediaList);
    const [_, useProductGenSku] = useMutation(PRODUCT_GEN_SKU);
    const [selectedSKUOption, setSelectedSKUOption] = useState<DropdownOptionType | undefined>();
    const [selectedSKUOptionTempStorage, setSelectedSKUOptionTempStorage] = useState<
      DropdownOptionType | undefined
    >();

    const [selectedPDTOption, setSelectedPDTOption] = useState<SelectOption<string> | undefined>();
    const [subSelectedPDTOption, setSubSelectedPDTOption] = useState<
      SelectOption<string> | undefined
    >();
    const useSubTypeBoolean = useBoolean(false);

    const applyProductTypeSelection = (productTypeKey?: string | null) => {
      if (!productTypeKey) {
        setSelectedPDTOption(undefined);
        setSubSelectedPDTOption(undefined);
        useSubTypeBoolean.setFalsy();
        return;
      }

      const foundCategory = categoriesStore?.categoriesV2?.find(
        (item) => item.key === productTypeKey,
      );

      if (!foundCategory) {
        setSubSelectedPDTOption(undefined);
        useSubTypeBoolean.setFalsy();
        return;
      }

      const foundMainCategory = categoriesStore?.categoriesV2?.find(
        (item) => item.key === foundCategory.mainTypeKey,
      );

      if (foundMainCategory) {
        setSelectedPDTOption({ label: foundMainCategory.label, value: foundMainCategory.key });
        setSubSelectedPDTOption({ label: foundCategory.label, value: foundCategory.key });
      } else {
        setSelectedPDTOption({ label: foundCategory.label, value: foundCategory.key });
        setSubSelectedPDTOption(undefined);
      }
    };
    const [titleValue, setTitleValue] = useState("");
    const [descriptionValue, setDescriptionValue] = useState<string>("");
    const [noBGImageForBlink, setNoBGImageForBlink] = useState<{
      image: CanvasDrawImageSource;
      sizes: Omit<PostRemoveBackgroundResponseData, "base64_str" | "statusCode">;
      centerDiff: Vec2;
    } | null>(null);
    const [delList, setDelList] = useState<string[]>([]);
    const skuSelectRef = useRef<any>();
    const [isSaveTriggered, setIsSaveTriggered] = useState(false);
    const [saveTitle, setSaveTitle] = useState<
      "save-product" | "save-video-only" | "save-photo-only"
    >("save-product");
    const proUserUpgradeBool = useBoolean(false);

    if (queryProductId) {
      const useProductFetch = useFetch(getProduct.setParams({ productId: queryProductId as any }));
      useProductFetch.onFetchSuccess((data) => {
        const option = { label: data.title, value: data._id };
        setSelectedSKUOption(option);

        applyProductTypeSelection(data.product_type_key);
        setTitleValue(data?.detailedTitle || "");
        setDescriptionValue(data?.description || "");
      });
    }

    const [AIResult, executeAIMutation] = useMutation(AI_RECOGNITION_TYPE);
    const usePostClassifyImageCall = useCall(postClassifyImage);
    usePostClassifyImageCall.onCallSuccess(async (data) => {
      const foundCategory = categoriesStore.defaultCategories.find(
        (category) => category.name === getCategory(data.predicted_class),
      );
      if (!foundCategory || !noBGImageForBlink) return;

      const blinkMedia = await prepareBlink(foundCategory, noBGImageForBlink);

      setFinalMedias((prevState) => prevState.concat(blinkMedia));
      useInBlinkBoolean.setFalsy();
    });

    const medias = finalMedias.filter((item) => {
      return !delList.includes(item.element.src);
    });

    const isShowNoSaveProductInfo = () => {
      return medias.length === 1;
    };

    const isTitleError = titleValue.length > productTitleMaxLength;
    const isDescError = descriptionValue.length > productDescriptionMaxLength;

    useEffect(() => {
      useSubTypeBoolean.setValue(
        categoriesStore?.categoriesV2?.some(
          (item) => item.mainTypeKey === selectedPDTOption?.value,
        ),
      );
    }, [selectedPDTOption]);

    useEffect(() => {
      setTimeout(() => {
        if (
          captureMode === "video" &&
          mediaStore.sidebarOpened &&
          (mediaStore.savedStatus == 1 || mediaStore.savedStatus == 3)
        ) {
          useTooltipBoolean.setTruthy();
        } else {
          useTooltipBoolean.setFalsy();
        }
      }, 300);
    }, [mediaStore.sidebarOpened, mediaStore.savedStatus]);

    const cleanup = () => {
      closePreUpload();
      mediaStore.setSidebarOpened(false);
      setTitleValue("");
      setDescriptionValue("");
      onClose();
      cameraStore.setGemcamPreviewVideo(null);
    };

    const handleConfirmClose = () => {
      notificationStore.open({
        title: localeStore.t('camera["camera-preview-sidebar"]["close-confirm"].title'),
        message: "",
        confirmText: localeStore.t('camera["camera-preview-sidebar"]["close-confirm"].confirm'),
        cancelText: localeStore.t('camera["camera-preview-sidebar"]["close-confirm"].cancel'),
        onOk: () => {
          handleClose();
        },
      });
    };

    // 关闭右侧预览弹窗
    const handleClose = () => {
      setClose();
    };

    // 预先上传视频
    const preUpload = async () => {
      const mediaToUpload = mediaList.filter((media) => !media.blinkId);
      const videoToUpload = mediaToUpload.filter((media) => media.element.tagName === "VIDEO");
      if (videoToUpload && videoToUpload.length <= 0) {
        return;
      }
      const filesPromises = videoToUpload.map(async (media) => {
        const contentType = "video/mp4";
        let blob;
        media.blobData
          ? (blob = media.blobData)
          : (blob = await fetch(media.element.src).then((res) => res.blob()));
        const fileName = uniqId() + ".mp4";
        const file = new File([blob], fileName, { type: contentType });
        return {
          files: file,
          types:
            videoAngle === 360 && bluetoothDeviceSelected?.name
              ? MediaType.video360
              : MediaType.video,
        };
      });

      const files = await Promise.all(filesPromises);
      const data = mediaStore.preUploadFiles.length
        ? mediaStore.preUploadFiles.concat(files)
        : [files];

      pushDataLayerEvent({
        event: "gemhub:camera:media:preupload:started",
        event_params: {
          media_filenames: files.map((f) => f.files.name),
        },
      });

      mediaStore.setPreUploadFiles(data);
      mediaStore.uploadMediaSync(files);
      mediaStore.uploadRequest?.fetch();
    };

    // 中断视频上传
    const closePreUpload = () => {
      if (mediaStore.savedStatus === 0) {
        mediaStore.uploadRequest?.abort();
      }
    };

    const isLocalStorageSettingsAvailable = (blob?: Blob | string) => {
      if (
        cameraStore.localSaveSettings.isLocalSaveMode &&
        !cameraStore.isLocalStorageSettingsAvailable(blob)
      ) {
        const param = {
          base_path: cameraStore.localSaveSettings?.folderPath,
          reserved_disk_space: cameraStore.localSaveSettings?.miniStorageSpace,
          reserved_disk_space_unit: cameraStore.localSaveSettings?.storageSpaceUnit,
        };
        if (!cameraStore.driveInfo) {
          modalsStore.open("CustomNotificationModal", {
            icon: <SvgIcon icon={InvalidDirectorySVG} size={34} />,
            title: localeStore.t(
              'camera["local-save-settings-modal"].notification["error-location"]',
            ),
            message: localeStore.t(
              'camera["local-save-settings-modal"].notification["message-save"]',
            ),
            confirmAppearance: "primary",
            confirmText: localeStore.t('camera["local-save-settings-modal"].notification.ok'),
          });
          pushMediaLocalSaveLayerEvent("saveMediaError", {
            ...param,
            error_message: localeStore.t(
              'camera["local-save-settings-modal"].notification["error-location"]',
            ),
          });
        } else {
          modalsStore.open("CustomNotificationModal", {
            icon: <SvgIcon icon={InvalidDirectorySVG} size={34} />,
            title: localeStore.t('camera["local-save-settings-modal"].notification["error-space"]'),
            message: localeStore.t(
              'camera["local-save-settings-modal"].notification["message-save"]',
            ),
            confirmAppearance: "primary",
            confirmText: localeStore.t('camera["local-save-settings-modal"].notification.ok'),
          });
          pushMediaLocalSaveLayerEvent("saveMediaError", {
            ...param,
            error_message: localeStore.t(
              'camera["local-save-settings-modal"].notification["error-space"]',
            ),
          });
        }
        cameraStore.setIsLocalSaveMode(false);
        return false;
      }
      return true;
    };

    const saveLocalStorage = (fileName: string, blob: Blob | string, sku?: string) => {
      const param = {
        base_path: cameraStore.localSaveSettings?.folderPath,
        reserved_disk_space: !cameraStore.localSaveSettings?.useCustom
          ? cameraStore.localSaveSettings?.miniStorageSpace
          : defaultMiniStorageSpace.miniStorageSpace,
        reserved_disk_space_unit: !cameraStore.localSaveSettings?.useCustom
          ? cameraStore.localSaveSettings?.storageSpaceUnit
          : defaultMiniStorageSpace.Unit,
        use_custom_reserved_disk_space: !!cameraStore.localSaveSettings?.useCustom,
        local_media_filenames: fileName,
        product_sku: sku,
      };

      const isAvailable = isLocalStorageSettingsAvailable(blob);
      if (isAvailable) {
        cameraStore.saveLocalStorage(
          fileName,
          blob,
          () => pushMediaLocalSaveLayerEvent("saveMediaSuccess", param),
          (msg) => {
            console.warn(msg);
            if (cameraStore.localSaveSettings.isLocalSaveMode) {
              modalsStore.open("CustomNotificationModal", {
                icon: <SvgIcon icon={InvalidDirectorySVG} size={34} />,
                title: localeStore.t(
                  'camera["local-save-settings-modal"].notification["error-location"]',
                ),
                message: localeStore.t(
                  'camera["local-save-settings-modal"].notification["message-save"]',
                ),
                confirmAppearance: "primary",
                confirmText: localeStore.t('camera["local-save-settings-modal"].notification.ok'),
                onOk: () => cameraStore.setIsLocalSaveMode(false),
              });

              pushMediaLocalSaveLayerEvent("saveMediaError", {
                ...param,
                error_message: msg,
              });
            }
          },
        );
      }
    };

    const handleSkuConfirmation = async () => {
      if (isAutoSkuConfirmation) {
        modalsStore.open("GeneratedSkuModal", {
          productType: selectedPDTOption?.value || "Unknown",
          onSave: (value) => {
            setSelectedSKUOption(value);
            setIsSaveTriggered(true);
            skuSelectRef.current.setOptions([value]);
          },
          onEdit: () => skuSelectRef.current.focus(),
        });
      } else {
        useInSaveBoolean.setTruthy();
        let title = null;
        try {
          const result = await useProductGenSku({
            productTypeKey: subSelectedPDTOption?.value || selectedPDTOption?.value || "Unknown",
          });
          title = result?.data?.productGenSku?.data;
        } catch (e) {
          setIsSaveTriggered(true);
        }

        if (title) {
          const { success } = await postProduct.getRequest({ data: { title } }).fetch();
          if (success) {
            productsStore.sendCreateProductEvent(success.total_items);
            const option = { label: title, value: success.rows[0]._id };
            setSelectedSKUOption(option);
            skuSelectRef.current.setOptions([option]);
            setIsSaveTriggered(true);
          }
        }
      }
    };

    const imageSourceToBase64Jpg = (source: HTMLImageElement | HTMLVideoElement) => {
      const { width = 0, height = 0 } = source;
      const canvas2d = new Canvas2d({
        size: { width, height },
      });
      canvas2d.drawRect({
        x: 0,
        y: 0,
        width,
        height,
        fill: "#ffffff",
      });
      canvas2d.drawImage(source);
      const dataUrl = canvas2d.toDataURL("image/jpeg", 1.0);
      return dpi ? changeDpiDataUrl(dataUrl, dpi) : dataUrl;
    };

    const handleSave = async () => {
      if (!selectedSKUOption && !useNoSaveProductInfo.value) {
        await handleSkuConfirmation();
        return;
      }

      await cameraStore.getDriveInfo();
      useInSaveBoolean.setTruthy();

      const blinkMedia = finalMedias.find((media) => media.blinkId);

      let mediaToUpload = finalMedias.filter((media) => !media.blinkId);

      if (delList.length > 0) {
        mediaToUpload = mediaToUpload.filter((item) => {
          return !delList.includes(item.element.src);
        });
      }
      const filesPromise = mediaToUpload.map(async (media) => {
        const contentType = media.element.tagName === "IMG" ? "image/png" : "video/mp4";
        const fileExtension = media.element.tagName === "IMG" ? ".png" : ".mp4";
        const mediaType =
          media.element.tagName === "IMG"
            ? MediaType.image
            : videoAngle === 360 && bluetoothDeviceSelected?.name
            ? MediaType.video360
            : MediaType.video;

        const locaFileName =
          (selectedSKUOption?.value ? selectedSKUOption.label + "_" : "") + media.uuId;
        let blob: Blob;
        if (media.element.tagName === "IMG") {
          const isJpg = cameraStore.localSaveSettings.format === MediaDownloadType.jpg;
          blob = base64ToBlob(media.element.src.split(",")[1], contentType);
          const originalMedia = mediaList.find(
            (f) => f.uuId === media.uuId && f.previewSrc !== media.previewSrc,
          );
          if (originalMedia)
            saveLocalStorage(
              `${locaFileName}.${originalMedia.processd}.${cameraStore.localSaveSettings.format}`,
              isJpg ? imageSourceToBase64Jpg(originalMedia.element) : originalMedia.previewSrc,
              selectedSKUOption?.label,
            );

          saveLocalStorage(
            `${locaFileName}.${media.processd}.${cameraStore.localSaveSettings.format}`,
            isJpg ? imageSourceToBase64Jpg(media.element) : media.previewSrc,
            selectedSKUOption?.label,
          );
        } else {
          media.blobData
            ? (blob = media.blobData)
            : (blob = await fetch(media.element.src).then((res) => res.blob()));
          saveLocalStorage(
            `${locaFileName}.${media.processd}${fileExtension}`,
            blob,
            selectedSKUOption?.label,
          );
        }

        const fileName = `${media.uuId}.${media.processd}${fileExtension}`;
        const file = new File([blob], fileName, { type: contentType });

        const metaData: DeviceMetaDataModel = {
          cameraName: cameraStore?.usbDevice?.cameraName,
          cameraManufacturer: cameraStore?.usbDevice?.cameraManufacturer,
          cameraSerialNumber: cameraStore?.usbDevice?.cameraSerialNumber,
          isAiBackgroundRemoval: media.aiHandling === "AiBackgroundRemoval",
          isTransparentBackground: media.aiHandling === "AiBackgroundRemoval",
          isAiRetouch: media.aiHandling === "AiRetouch",
          isEclipseMode: openEclipse,
        };

        return {
          files: file,
          types: mediaType,
          sku: selectedSKUOption?.value,
          product_type_key: subSelectedPDTOption?.value || selectedPDTOption?.value,
          productType: subSelectedPDTOption?.name || selectedPDTOption?.name,
          title: titleValue,
          description: descriptionValue,
          metaData: metaData,
        };
      });

      try {
        if (blinkMedia?.blinkId && selectedSKUOption?.value) {
          await putAssignMediaToProduct
            .getRequest({
              params: {
                productId: selectedSKUOption.value,
              },
              queryParams: blinkMedia.blinkId.toString(),
            })
            .fetch();
        }
        const files = await Promise.all(filesPromise);
        pushDataLayerEvent({
          event: "click:camera:preview:save",
          event_params: {
            product_sku: files[0]?.sku,
            product_type: files[0]?.productType,
            media_filenames: (mediaStore.preUploadFiles.length
              ? mediaStore.preUploadFiles[0].concat(files.filter((d) => !d.types.includes("video")))
              : files
            ).map((f: { files: { name: any } }) => f.files.name),
          },
        });
        if (captureMode === "video") {
          mediaStore.setSavedPostUploadMedia(files);
          mediaStore.setSavedTitleValue(titleValue);
          mediaStore.setSavedDescriptionValue(descriptionValue);
          mediaStore.setSavedProductType(files[0]?.product_type_key);
          mediaStore.setSavedSelectedSKUOption(selectedSKUOption?.value);
          mediaStore.setSavedStatus(1);
          handleClose();
          useInSaveBoolean.setFalsy();
          if (mediaStore.preUploadStatus === 0 || mediaStore.preUploadStatus === 3) {
            mediaStore.setSavedProgress(0);
            preUpload();
          } else if (mediaStore.preUploadStatus === 2) {
            mediaStore.handleSave();
          }
        } else {
          pushDataLayerEvent({
            event: "gemhub:camera:media:cloud_save:started",
            event_params: {
              product_sku: files[0]?.sku,
              product_type: files[0]?.productType,
              media_filenames: files.map((f) => f.files.name),
              run_in_background: false,
            },
          });
          const parameters = getAttributesForRequest(
            {
              detailedTitle: titleValue || "",
              description: descriptionValue || "",
              product_type_kay: files[0]?.product_type_key || "",
            },
            attributesStore.attributes,
          ).filter((item) => item.value);
          mediaStore
            .uploadMedia(
              files,
              { limit: 1 },
              {
                description: descriptionValue,
                detailedTitle: titleValue,
                parameters,
              },
            )
            .then((res) => {
              if (res.status === "success") {
                pushDataLayerEvent({
                  event: "gemhub:camera:media:cloud_save:success",
                  event_params: {
                    product_sku: files[0]?.sku,
                    product_type: files[0]?.productType,
                    media_filenames: files.map((f) => f.files.name),
                  },
                });
                handleClose();
              } else {
                pushDataLayerEvent({
                  event: "gemhub:camera:media:cloud_save:error",
                  event_params: {
                    product_sku: files[0]?.sku,
                    product_type: files[0]?.productType,
                    media_filenames: files.map((f) => f.files.name),
                    http_status_code: res.details.statusCode,
                    error_message: res.error,
                  },
                });
                if (res.error?.originalError.code !== "limit:storage_exceeded") {
                  notificationStore.open({
                    title: "Saving failed, please retry",
                    confirmText: "RETRY",
                    cancelText: "",
                    confirmAppearance: "secondary",
                    icon: "exclamation",
                    onlyConfirm: true,
                  });
                }
              }
              useInSaveBoolean.setFalsy();
            });
        }
      } catch (e) {
        console.error(e);
        notificationStore.open({
          title: localeStore.t('camera["camera-preview-sidebar"]["error-popup"].title'),
          confirmText: localeStore.t('camera["camera-preview-sidebar"]["error-popup"]["ok-text"]'),
          cancelText: "",
          confirmAppearance: "secondary",
          icon: "exclamation",
          onlyConfirm: true,
        });
        useInSaveBoolean.setFalsy();
      }
    };

    const handleSKUChange = (value: DropdownOptionType | undefined) => {
      if (value?.source) {
        const source = value.source;
        applyProductTypeSelection(source.product_type_key);
        setTitleValue(source?.detailedTitle || "");
        setDescriptionValue(source?.description || "");
      }
      setSelectedSKUOption(value);
    };

    const handlePDTChange = (value: SelectOption<string> | undefined) => {
      setSelectedPDTOption(value);
      if (value?.label !== selectedPDTOption?.label) {
        setSubSelectedPDTOption(undefined);
      }
    };

    const handlePDTChangeSub = (value: SelectOption<string> | undefined) => {
      setSubSelectedPDTOption(value);
    };

    const handleTitleChange = (value: string) => setTitleValue(value);

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setDescriptionValue(e.target.value);
    };

    const videoThumbnailBase64 = useMemo(() => {
      const media = mediaList.find((media) =>
        captureMode === "video" ? media.element.tagName !== "IMG" : media.element.tagName === "IMG",
      );
      if (!media) return null;
      return media.previewSrc;
    }, [mediaList]);

    const delayPromise = async (item: CameraMediaType) => {
      return new Promise<CameraMediaType>((resolve, reject) => {
        setTimeout(() => {
          const func = (media: CameraMediaType) =>
            removeImageBackground(media.element, { directRequest: true })
              .then(async ({ image }) => {
                const canvasImg = imageSourceToCanvas(image);
                const logoWatermarkBase64 = await cameraStore.setLogoWatermarkToImage(
                  canvasImg.toDataURL(),
                );
                const base64 = dpi
                  ? changeDpiDataUrl(logoWatermarkBase64, dpi)
                  : logoWatermarkBase64;
                return base64;
              })
              .then(async (base64) => {
                const image = await loadImage(base64);
                resolve({
                  element: image,
                  previewSrc: base64,
                  uuId: media.uuId,
                  processd: "background",
                  aiHandling: "AiBackgroundRemoval",
                });
              })
              .catch((err) => {
                console.error("remove bg error: ", err);
                reject(media);
              });

          return func(item);
        }, 50);
      });
    };

    const handleRemoveMediaThumbnail = async () => {
      //mounting if in model image mode
      if (!isBurstMode || presetMode !== "model" || !videoThumbnailBase64) return;

      useInBlinkBoolean.setTruthy();

      const mediaThumbnail = await loadImage(videoThumbnailBase64, true);

      const noBGImage = await removeImageBackground(mediaThumbnail, {
        cropped: true,
        directRequest: true,
      });

      setNoBGImageForBlink(noBGImage);

      const noBGCanvas = imageSourceToCanvas(noBGImage.image);

      const noBGCanvasBase64 = dpi
        ? changeDpiDataUrl(noBGCanvas.toDataURL(), dpi)
        : noBGCanvas.toDataURL();
      const blob = base64ToBlob(noBGCanvasBase64.split(",")[1], "image/jpeg");
      const fileName = uniqId() + ".jpeg";
      const file = new File([blob], fileName, { type: "image/jpeg" });

      const formData = new FormData();
      formData.append("file", file);

      return usePostClassifyImageCall.submit({ data: formData });
    };

    const handleRemoveMediaListBg = async () => {
      //mounting if in remove background mode
      if (!isBurstMode || presetMode !== "background" || openEclipse) return;

      useInRmBGBoolean.setTruthy();
      const medias = mediaList.filter((media) => media.element.tagName === "IMG");
      if (!medias.length) return;

      const removeBGPromises = [];
      let isRemoveBackgroundError = false;
      for (const item of medias) {
        removeBGPromises.push(
          delayPromise(item).catch((originalImage) => {
            isRemoveBackgroundError = true;
            return originalImage;
          }),
        );
      }

      try {
        const res = await Promise.all(removeBGPromises);

        setFinalMedias((prevState) => {
          useInRmBGBoolean.setFalsy();
          return prevState
            .filter((prevItem) => prevItem.element.tagName !== "IMG")
            .concat(res as any);
        });
      } catch (error) {
        console.error(error);
        isRemoveBackgroundError = true;
      }
      if (isRemoveBackgroundError)
        notificationStore.open({
          title: localeStore.t('camera["camera-preview-sidebar"]["error-remove-bg"].title'),
          confirmText: localeStore.t("common.buttons.confirm"),
          cancelText: "",
          confirmAppearance: "secondary",
          icon: "exclamation",
          onlyConfirm: true,
        });

      return;
    };

    const handleRetouchAI = async () => {
      //mounting if in ai retouch mode
      if (!isBurstMode || presetMode !== "retouch" || openEclipse) return;

      const medias = mediaList.filter((media) => media.element.tagName === "IMG");
      if (!medias.length) return;
      useInRmBGBoolean.setTruthy();
      const retouchPromises = medias.map((media) => {
        const oldCanvas = imageSourceToCanvas(media.element);
        const base64 = oldCanvas.toDataURL();
        const blob = base64ToBlob(base64.split(",")[1], "image/png");

        const formData = new FormData();
        formData.append("files", blob);

        return postRetouchAISingle
          .getRequest({ data: formData })
          .fetch()
          .then(async (result) => {
            let retouchedImg: CanvasDrawImageSource = await loadImage(
              `data:image/png;base64,${result.success}`,
              true,
            );

            // NOTE: retouch AI is limited to 1536x1536 maximum resolution,
            // so resize image to source size before actual retouching
            if (
              retouchedImg.width !== oldCanvas.width ||
              retouchedImg.height !== oldCanvas.height
            ) {
              retouchedImg = resizeImage(retouchedImg, oldCanvas.width, oldCanvas.height);
            }
            const retouchedCanvas = imageSourceToCanvas(retouchedImg);
            const logoWatermarkBase64 = await cameraStore.setLogoWatermarkToImage(
              retouchedCanvas.toDataURL(),
            );
            const base64 = dpi ? changeDpiDataUrl(logoWatermarkBase64, dpi) : logoWatermarkBase64;
            const retouchedElement = await loadImage(base64, true);
            return {
              element: retouchedElement,
              previewSrc: base64,
              aiHandling: "AiRetouch",
              uuId: media.uuId,
              processd: "retouch",
            } as CameraMediaType;
          })
          .catch(() => {
            useInRmBGBoolean.setFalsy();
            return media;
          });
      });

      try {
        const res = await Promise.all<CameraMediaType>(retouchPromises);
        useInRmBGBoolean.setFalsy();
        setFinalMedias((prevState) => {
          return prevState
            .filter((prevItem) => prevItem.element.tagName !== "IMG")
            .concat(res as any);
        });
      } catch (error) {
        console.error(error);
      }

      return;
    };

    const isGptLimitExceeded = () => {
      if (
        !userStore.isSubscribed &&
        (userStore.userUsage?.countGPTDescriptionsAttemptMonth ?? 0) >=
          subscriptionsStore.limits.countGPTDescription
      ) {
        proUserUpgradeBool.setTruthy();
        return true;
      }
      return false;
    };

    const handleDescriptionAI = async (isTrigger: boolean = false) => {
      if (!isTrigger) {
        if (!isBurstMode || !generateAIDescription || titleValue || !videoThumbnailBase64) return;
      }

      if (isGptLimitExceeded()) return;

      const sendCreateDescriptionAI = async (isReplace: boolean = false) => {
        const blob = base64ToBlob(videoThumbnailBase64!.split(",")[1], "image/jpeg");
        const fileName = uniqId() + ".jpeg";
        const file = new File([blob], fileName, { type: "image/jpeg" });
        const variables = { file, language: userStore.aiLanguageV2 };
        const requestResult = await executeAIMutation(variables);
        if (requestResult?.data?.aiRecognition) {
          const data = requestResult.data.aiRecognition;
          userStore.loadUserMeSilently();
          if (!titleValue || isReplace) setTitleValue(data.title);
          if (!descriptionValue || isReplace) setDescriptionValue(data.description);
          if ((!selectedPDTOption || isReplace) && data?.recognition?.type) {
            applyProductTypeSelection(data?.recognition?.subType || data?.recognition?.type);
          }
        }
      };

      if (selectedPDTOption && titleValue && descriptionValue) {
        modalsStore.open("CustomNotificationModal", {
          title: localeStore.t(
            'camera["camera-preview-sidebar"]["ai-product-description-modal"].title',
          ),
          contentClassName: styles.descriptionAIContentModal,
          titleClassName: styles.descriptionAITitleModal,
          confirmText: localeStore.t(
            'camera["camera-preview-sidebar"]["ai-product-description-modal"]["confirm-text"]',
          ),
          cancelText: localeStore.t(
            'camera["camera-preview-sidebar"]["ai-product-description-modal"]["cancel-text"]',
          ),
          withCross: true,
          onOk: () => {
            setSelectedPDTOption(undefined);
            setTitleValue("");
            setDescriptionValue("");
            sendCreateDescriptionAI(true).finally(() => {
              useDescAIHandleLoadingBoolean.setFalsy();
            });
          },
        });
        return;
      }
      return sendCreateDescriptionAI();
    };

    const generateUsageInfo = (limitsCount: number, usedCount?: number | null) => {
      if (!userStore.isSubscribed) {
        return (
          <span className={styles.aiDescriptionCount}>
            {Math.max(0, limitsCount - (usedCount ?? 0))}/{limitsCount}
          </span>
        );
      }
      return <SvgIcon icon={ProSVG} size={[26, 14]} />;
    };

    const handleNoSaveProductInfoChange = () => {
      if (!useNoSaveProductInfo.value) {
        modalsStore.open("CustomNotificationModal", {
          contentClassName: styles.contentNoSaveProductInfo,
          title: (
            <span className={styles.titleNoSaveProductInfo}>
              {localeStore.t('camera["camera-preview-sidebar"]["not-save-product-modal"].title')}
            </span>
          ),
          message: (
            <span className={styles.messageNoSaveProductInfo}>
              {localeStore.t('camera["camera-preview-sidebar"]["not-save-product-modal"].message')}
            </span>
          ),
          confirmAppearance: "primary",
          confirmText: localeStore.t("common.buttons.confirm"),
          cancelText: localeStore.t("common.buttons.cancel"),
          withCross: true,
          onOk: () => {
            cameraStore.setIsNoSaveProductInfo(true);
            useNoSaveProductInfo.setTruthy();
          },
        });
      } else {
        cameraStore.setIsNoSaveProductInfo(false);
        useNoSaveProductInfo.setFalsy();
      }
    };

    const onDeleteFn = (imageSrc: string) => {
      setDelList([...delList, imageSrc]);
    };

    useDidUpdate(
      () => {
        if (!loading && videoThumbnailBase64) {
          setFinalMedias(() => mediaList);
          if (mediaStore.savedStatus === 0 || mediaStore.savedStatus === 2) {
            preUpload();
          }
          if (!gemAIActive || !userStore.isSubscribed) {
            useAIHandleLoadingBoolean.setFalsy();
            useDescAIHandleLoadingBoolean.setFalsy();
            return;
          }
          Promise.all([
            handleRemoveMediaThumbnail(),
            handleRemoveMediaListBg(),
            handleRetouchAI(),
            handleDescriptionAI().finally(() => {
              useDescAIHandleLoadingBoolean.setFalsy();
            }),
          ])
            .then(() => {
              useAIHandleLoadingBoolean.setFalsy();
            })
            .catch(() => {
              useAIHandleLoadingBoolean.setFalsy();
            });
        }
      },
      [loading, videoThumbnailBase64],
      true,
    );

    useDidUpdate(() => {
      if (isSaveTriggered && selectedSKUOption) {
        handleSave();
        setIsSaveTriggered(false);
      }

      if (selectedSKUOption) {
        setSelectedSKUOptionTempStorage(undefined);
        useNoSaveProductInfo.setFalsy();
      }
    }, [selectedSKUOption, isSaveTriggered]);

    useDidUpdate(() => {
      if (useNoSaveProductInfo.value) {
        setSelectedSKUOptionTempStorage(selectedSKUOption);
        setSelectedSKUOption(undefined);
      } else {
        if (selectedSKUOptionTempStorage) {
          setSelectedSKUOption(selectedSKUOptionTempStorage);
          setSelectedSKUOptionTempStorage(undefined);
        }
      }
    }, [useNoSaveProductInfo.value]);

    useDidUpdate(
      () => {
        if (selectedSKUOption?.value || !useNoSaveProductInfo.value) {
          setSaveTitle("save-product");
        } else if (isShowNoSaveProductInfo()) {
          const tagName = medias[0].element.tagName;
          setSaveTitle(tagName === "IMG" ? "save-photo-only" : "save-video-only");
        }
      },
      [isShowNoSaveProductInfo(), selectedSKUOption, useNoSaveProductInfo.value],
      true,
    );

    useDidUpdate(
      () => {
        if (medias.length > 1) useNoSaveProductInfo.setFalsy();
      },
      [medias.length],
      true,
    );

    useDidUnmount(() => {
      cleanup();
    });

    const saveButtonLoading =
      (AIResult.fetching && !useNoSaveProductInfo.value) ||
      useInSaveBoolean.value ||
      useInBlinkBoolean.value ||
      useAIHandleLoadingBoolean.value ||
      loading;

    const descAIHandleLoading =
      AIResult.fetching || (!!generateAIDescription && useDescAIHandleLoadingBoolean.value);

    return (
      <ExpandableSidebar
        portalClassName={styles.portal}
        contentClassName={styles.contentWrapper}
        sidebarHeaderClassName={styles.headerContainer}
        sidebarContentClassName={styles.contentContainer}
        sidebarFooterClassName={styles.footerContainer}
        iconPos="outside"
        title={localeStore.t('camera["camera-preview-sidebar"].title')}
        isOpen={isOpen}
        onFinalClosed={onFinalClosed}
        footer={
          <div className={styles.footer}>
            <Button
              appearance="tertiaryOutlined"
              size="large"
              onClick={handleConfirmClose}
              disabled={useInSaveBoolean.value}
            >
              {localeStore.t('camera["camera-preview-sidebar"].buttons.cancel')}
            </Button>

            <Tooltip
              variant="solid"
              id="save-btn-tooltip"
              sx={{
                width: "233px",
                fontSize: "13px",
                textAlign: "center",
                background: "#363E5C",
                borderRadius: "4px",
                padding: "10px",
              }}
              title={
                <div className="save-btn-tooltip">
                  <span
                    className="close"
                    onClick={() => {
                      useTooltipBoolean.setFalsy();
                    }}
                  >
                    &times;
                  </span>
                  {mediaStore.savedStatus === 1 &&
                    localeStore.t('camera["save-background"]["save-tip"]')}
                  {mediaStore.savedStatus === 3 &&
                    localeStore.t('camera["save-background"]["error-tip"]')}
                </div>
              }
              arrow
              open={
                useTooltipBoolean.value &&
                (mediaStore.savedStatus === 1 || mediaStore.savedStatus === 3)
              }
              placement="top"
            >
              <div style={{ width: "100%" }}>
                <Button
                  disabled={isTitleError || isDescError}
                  size="large"
                  onClick={handleSave}
                  className={
                    mediaStore.savedStatus === 1 || mediaStore.savedStatus === 3
                      ? "save-btn-disabled"
                      : ""
                  }
                  loading={saveButtonLoading}
                >
                  {localeStore.t(`camera["camera-preview-sidebar"].buttons.saves["${saveTitle}"]`)}
                </Button>
              </div>
            </Tooltip>
          </div>
        }
        disableBackdropClose={true}
      >
        <div className={styles.content}>
          <MediaPreview
            mediaList={finalMedias}
            onDelete={onDeleteFn}
            setClose={setClose}
            hideAddButton={true}
            showLoadingPreview={useInBlinkBoolean.value}
            showLoadingRmBG={useInRmBGBoolean.value}
          />
          <div className={styles.formItemWrap}>
            <SKUSelect
              ref={skuSelectRef}
              selectedSKUOption={selectedSKUOption}
              onSKUChange={handleSKUChange}
              required={!useNoSaveProductInfo.value}
            />
            {isShowNoSaveProductInfo() && (
              <Checkbox
                className={styles.skuCheckboxWrap}
                onClick={(e) => e.stopPropagation()}
                onChange={handleNoSaveProductInfoChange}
                label={localeStore.t('camera["camera-preview-sidebar"]["not-save-product"]')}
                checked={useNoSaveProductInfo.value}
                disableError
              />
            )}
          </div>
          {!useNoSaveProductInfo.value && (
            <React.Fragment>
              {!descAIHandleLoading && (
                <Button
                  className={styles.btnAiDescription}
                  appearance="primaryOutlined"
                  onClick={() => {
                    handleDescriptionAI(true).finally(() => {
                      useDescAIHandleLoadingBoolean.setFalsy();
                    });
                  }}
                >
                  <SvgIcon icon={AiDescriptionSVG} size={20} />
                  <span>
                    {localeStore.t(
                      'camera["camera-preview-sidebar"]["ai-product-description-modal"]["add-ai-description"]',
                    )}
                  </span>
                  {generateUsageInfo(
                    subscriptionsStore.limits.countGPTDescription,
                    userStore.userUsage?.countGPTDescriptionsAttemptMonth,
                  )}
                </Button>
              )}
              <div className={styles.formItemWrap}>
                {descAIHandleLoading && (
                  <div className={styles.loadingBox}>
                    <span className={styles.loadingPoint}></span>
                    <span className={styles.loadingPoint}></span>
                    <span className={styles.loadingPoint}></span>
                  </div>
                )}

                <ProductTypeSelect
                  disabled={descAIHandleLoading}
                  selectedOption={selectedPDTOption}
                  onChange={handlePDTChange}
                ></ProductTypeSelect>
              </div>

              {useSubTypeBoolean.value && (
                <div className={styles.formItemWrap}>
                  {descAIHandleLoading && (
                    <div className={styles.loadingBox}>
                      <span className={styles.loadingPoint}></span>
                      <span className={styles.loadingPoint}></span>
                      <span className={styles.loadingPoint}></span>
                    </div>
                  )}

                  <ProductSubTypeSelect
                    disabled={descAIHandleLoading}
                    mainSelectedOption={selectedPDTOption}
                    selectedOption={subSelectedPDTOption}
                    onChange={handlePDTChangeSub}
                  ></ProductSubTypeSelect>
                </div>
              )}

              <div className={styles.formItemWrap}>
                {descAIHandleLoading && (
                  <div className={styles.loadingBox}>
                    <span className={styles.loadingPoint}></span>
                    <span className={styles.loadingPoint}></span>
                    <span className={styles.loadingPoint}></span>
                  </div>
                )}
                <Input
                  appearance="primaryV2"
                  className={styles.titleField}
                  label={localeStore.t('camera["camera-preview-sidebar"]["title-field"].label')}
                  placeholder={
                    !descAIHandleLoading
                      ? localeStore.t('camera["camera-preview-sidebar"]["title-field"].placeholder')
                      : ""
                  }
                  value={titleValue}
                  onChange={handleTitleChange}
                  disableError={!isTitleError}
                  data-hj-allow
                  error={
                    isTitleError &&
                    localeStore
                      .t('camera["camera-preview-sidebar"]["title-field"].error')
                      .replace("{count}", productTitleMaxLength + "")
                  }
                />
              </div>

              <div className={styles.formItemWrap}>
                {descAIHandleLoading && (
                  <div className={styles.loadingBox}>
                    <span className={styles.loadingPoint}></span>
                    <span className={styles.loadingPoint}></span>
                    <span className={styles.loadingPoint}></span>
                  </div>
                )}
                <label className={styles.descriptionLabel}>
                  <Typography size="extraSmall" color="textSecondary">
                    {localeStore.t('camera["camera-preview-sidebar"]["description-field"].label')}
                  </Typography>
                </label>
                <textarea
                  className={clsx(styles.descriptionField, globalStyles.addScrollStyles, {
                    [styles.descriptionError]: isDescError,
                  })}
                  id="description"
                  placeholder={
                    !descAIHandleLoading
                      ? localeStore.t(
                          'camera["camera-preview-sidebar"]["description-field"].placeholder',
                        )
                      : ""
                  }
                  value={descriptionValue}
                  onChange={handleDescriptionChange}
                  data-hj-allow
                />
                {isDescError && (
                  <label className={styles.descriptionErrorLabel}>
                    {localeStore
                      .t('camera["camera-preview-sidebar"]["description-field"].error')
                      .replace("{count}", productDescriptionMaxLength + "")}
                  </label>
                )}
              </div>
            </React.Fragment>
          )}
        </div>
        <ProUserUpgradeModal
          visible={proUserUpgradeBool}
          onClose={proUserUpgradeBool.setFalsy}
          content={localeStore.t('common["pro-user"]["description-upgrade-text"]')}
        ></ProUserUpgradeModal>
      </ExpandableSidebar>
    );
  },
);
